<template>
  <div class="ptpm-backdrop" @click.self="onClose">
    <div class="ptpm-modal" role="dialog" aria-modal="true">
      <header class="ptpm-header">
        <div>
          <div class="ptpm-eyebrow">Treatment plans</div>
          <div class="ptpm-title" v-if="patient">
            {{ patient.fName }} {{ patient.lName }}
            <span
              v-if="patientStatusBadge"
              class="ptpm-status-badge"
              :title="`Marked ${patientStatusBadge} in Open Dental`"
            >{{ patientStatusBadge }}</span>
          </div>
          <div class="ptpm-title" v-else>Select a plan</div>
        </div>
        <button class="ptpm-close" @click="onClose" aria-label="Close">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <div class="ptpm-body">
        <div v-if="error" class="ptpm-error">{{ error }}</div>

        <div v-if="loading" class="ptpm-loading">
          <i class="fa fa-circle-o-notch fa-spin"></i>
          <span>Loading plans…</span>
        </div>

        <template v-else>
          <!-- Suppressed when `error` is set: "no plans exist" is a claim about the
               patient, and after a failed load we don't know that — showing both at
               once told TCs the wrong thing about the wrong patient. -->
          <div v-if="plans.length === 0 && !error" class="ptpm-empty">
            <i class="fa fa-folder-open-o"></i>
            <p>No saved or active treatment plans for this patient.</p>
            <p class="ptpm-empty-sub">
              Plans are created in Open Dental — once one exists, it'll show up here.
            </p>
          </div>

          <template v-else>
            <p class="ptpm-help">
              <strong>Present</strong> opens the plan chairside on this device.
              <strong>Send</strong> texts or emails the patient a link to review at home.
            </p>

            <ul class="ptpm-plans">
              <li
                v-for="p in plansSorted"
                :key="p.treatPlanNum"
                class="ptpm-plan"
                :class="{ 'ptpm-plan-active': p.status === 1 }"
              >
                <div class="ptpm-plan-main">
                  <div class="ptpm-plan-title">
                    {{ p.heading || (p.status === 1 ? 'Active Plan' : 'Saved Plan') }}
                  </div>
                  <div class="ptpm-plan-meta">
                    <span class="ptpm-plan-status" :class="p.status === 1 ? 'is-active' : 'is-saved'">
                      <i class="fa" :class="p.status === 1 ? 'fa-circle' : 'fa-circle-o'"></i>
                      {{ p.status === 1 ? 'Active' : 'Saved' }}
                    </span>
                    <span v-if="p.dateTP" class="ptpm-plan-date">
                      <i class="fa fa-calendar-o"></i>
                      Created {{ formatDate(p.dateTP) }}
                    </span>
                  </div>
                </div>
                <div class="ptpm-plan-actions">
                  <button
                    v-if="!isTouchDevice"
                    class="ptpm-act ptpm-act-ghost ptpm-act-icon"
                    @click="onPresentOnTablet(p)"
                    title="Present on Tablet"
                    aria-label="Present on Tablet"
                  >
                    <i class="fa fa-qrcode"></i>
                  </button>
                  <button class="ptpm-act ptpm-act-primary" @click="onPresent(p)" title="Present chairside now">
                    <i class="fa fa-desktop"></i> Present
                  </button>
                  <button class="ptpm-act ptpm-act-ghost" @click="onSelect(p)" title="Send to patient">
                    <i class="fa fa-paper-plane"></i> Send
                  </button>
                </div>
              </li>
            </ul>
          </template>
        </template>
      </div>

      <footer class="ptpm-footer">
        <button class="ptpm-btn" @click="onClose">Close</button>
      </footer>
    </div>

    <PresentOnTabletModal
      v-if="tabletPlan && patient"
      :pat-num="patient.patNum"
      :treat-plan-num="tabletPlan.treatPlanNum"
      :patient-name="patientFullName"
      :patient-dob="patient.birthdate ? formatDate(patient.birthdate) : ''"
      :plan-heading="tabletPlan.heading || (tabletPlan.status === 1 ? 'Active Plan' : 'Saved Plan')"
      @close="tabletPlan = null"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import PresentOnTabletModal from './PresentOnTabletModal.vue';
import { isTouchPrimaryDevice } from '@/utils/deviceType.js';
import { patientStatusLabel } from '../constants/patientStatus.js';

export default {
  name: 'PatientTxPlansModal',
  components: { PresentOnTabletModal },
  data() {
    return { tabletPlan: null };
  },
  computed: {
    ...mapState('txPresenter', { dispatcher: (s) => s.patientTxPlansDispatcher }),
    patient() { return this.dispatcher.patient; },
    plans() { return this.dispatcher.plans || []; },
    loading() { return this.dispatcher.loading; },
    error() { return this.dispatcher.error; },
    // Hide the QR hand-off when we're already on a tablet/phone — just Present.
    isTouchDevice() { return isTouchPrimaryDevice(); },
    patientFullName() {
      const p = this.patient;
      return p ? `${p.fName || ''} ${p.lName || ''}`.trim() : '';
    },
    // Null (no badge) for Active patients, and for an older backend that doesn't
    // send patStatus yet — so this is safe to ship ahead of the service.
    patientStatusBadge() {
      return patientStatusLabel(this.patient && this.patient.patStatus);
    },
    plansSorted() {
      // Active first, then by dateTP desc (most recent first). Lets the TC
      // hit the "right" plan without scanning the whole list.
      const arr = [...this.plans];
      arr.sort((a, b) => {
        if (a.status !== b.status) return b.status - a.status;
        const ad = a.dateTP ? new Date(a.dateTP).getTime() : 0;
        const bd = b.dateTP ? new Date(b.dateTP).getTime() : 0;
        return bd - ad;
      });
      return arr;
    },
  },
  methods: {
    ...mapActions('txPresenter', ['closePatientTxPlansDispatcher', 'selectPatientPlanForSend']),
    onClose() {
      this.closePatientTxPlansDispatcher();
    },
    onSelect(plan) {
      this.selectPatientPlanForSend({ treatPlanNum: plan.treatPlanNum });
    },
    // Open the QR hand-off so the team member can scan into Present Mode on a
    // tablet/phone. Keeps the dispatcher open behind the QR overlay.
    onPresentOnTablet(plan) {
      this.tabletPlan = plan;
    },
    // One-tap chairside present — close the dispatcher and jump straight into
    // full-screen Present Mode (standalone route that bootstraps its own data).
    onPresent(plan) {
      const patNum = this.patient && this.patient.patNum;
      if (!patNum || !plan || !plan.treatPlanNum) return;
      this.closePatientTxPlansDispatcher();
      this.$router.push({
        name: 'TxPresenterPresentMode',
        params: { patNum: String(patNum), treatPlanNum: String(plan.treatPlanNum) },
      });
    },
    formatDate(d) {
      if (!d) return '';
      const m = moment(d);
      return m.isValid() ? m.format('MMM D, YYYY') : '';
    },
  },
};
</script>

<style scoped>
.ptpm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 28px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.ptpm-modal {
  background: #fff;
  width: 100%;
  max-width: 540px;
  max-height: calc(100vh - 56px);
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ptpm-modal * { box-sizing: border-box; }

.ptpm-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e4e7ec;
}
.ptpm-eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #94a3b8;
  font-weight: 600;
}
.ptpm-title {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
  margin-top: 4px;
}
/* Non-Active OD patient status (Inactive / Archived / Deceased / …). Amber, not
   red — it's information the TC should see before sending, not an error. */
.ptpm-status-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #fdf3d7;
  color: #8a6d1a;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  vertical-align: middle;
}
.ptpm-close {
  margin-left: auto;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #475569;
  cursor: pointer;
}
.ptpm-close:hover { background: #f1f3f6; }

.ptpm-body {
  padding: 16px 22px;
  overflow-y: auto;
  flex: 1;
}

.ptpm-error {
  padding: 8px 12px;
  border-radius: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 12.5px;
  margin-bottom: 12px;
}

.ptpm-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 30px;
  justify-content: center;
  color: #475569;
  font-size: 13px;
}

.ptpm-empty {
  text-align: center;
  padding: 36px 18px;
  color: #475569;
}
.ptpm-empty .fa { font-size: 28px; color: #cbd5e1; margin-bottom: 10px; }
.ptpm-empty p { margin: 0; font-size: 13.5px; }
.ptpm-empty-sub {
  margin-top: 6px !important;
  font-size: 12px !important;
  color: #94a3b8;
}

.ptpm-help {
  font-size: 12.5px;
  color: #475569;
  margin: 0 0 14px;
  line-height: 1.5;
}

.ptpm-plans {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ptpm-plan {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #f7f8fa;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
}
.ptpm-plan-active {
  background: #ecf5f3;
  border-color: #b9dcd5;
}

.ptpm-plan-main { flex: 1; min-width: 0; }
.ptpm-plan-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.1px;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ptpm-plan-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  font-size: 11.5px;
  color: #475569;
}
.ptpm-plan-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 10px;
}
.ptpm-plan-status.is-active {
  background: #dcfce7;
  color: #166534;
}
.ptpm-plan-status.is-active .fa { font-size: 8px; color: #16a34a; }
.ptpm-plan-status.is-saved {
  background: #fef3c7;
  color: #92400e;
}
.ptpm-plan-status.is-saved .fa { font-size: 8px; color: #d97706; }
.ptpm-plan-date {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #94a3b8;
}
.ptpm-plan-date .fa { font-size: 11px; }

.ptpm-plan-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ptpm-act {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.1s, border-color 0.1s;
}
.ptpm-act .fa { font-size: 11px; }
/* Icon-only QR hand-off button — keeps the row compact alongside Present/Send. */
.ptpm-act-icon { padding: 7px 10px; }
.ptpm-act-icon .fa { font-size: 14px; }
.ptpm-act-primary {
  background: #0d5f54;
  color: #fff;
}
.ptpm-act-primary:hover { background: #094840; }
.ptpm-act-ghost {
  background: #fff;
  color: #0d5f54;
  border-color: #b9dcd5;
}
.ptpm-act-ghost:hover { background: #ecf5f3; }

.ptpm-footer {
  padding: 12px 22px;
  border-top: 1px solid #e4e7ec;
  display: flex;
  justify-content: flex-end;
  background: #fafafa;
}
.ptpm-btn {
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.ptpm-btn:hover { background: #f1f3f6; }
</style>
